import { useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { useDispatch, useSelector } from "react-redux";
import { ComponentToPrint } from "./ComponentToPrint";

import ApiHelper from "../../../Helpers/ApiHelper";
import Overlay from "../../SharedComponents/Overlay";
import WholePdfFile from "./PdfFile/WholePdfFile";
import DownloadButtonUi from "./DownloadButtonUi";
import AskAiButtonUi from "./AskAiButtonUi";
import AiResponseModal from "./AiResponseModal";
import { generatePdfBlob } from "../Helpers/generatePdfBlob";
import "../Styles/PdfFile.css";
import { UpdateAlertModalData } from "../../SharedComponents/GeneralModals/Store";

export default function TestPdfButton({ testResultId, resultData, uiType, docTitle }) {
  const user = useSelector((state) => state.auth.user);

  const dispatch = useDispatch();

  const [showOverlay, setShowOverlay] = useState(false);
  const [pdfComponent, setPdfComponent] = useState(null);
  const componentRef = useRef(null);

  // AI Analysis state
  const [showAiModal, setShowAiModal] = useState(false);
  const [autoUploadFile, setAutoUploadFile] = useState(null);
  const [isGeneratingForAi, setIsGeneratingForAi] = useState(false);
  const aiPdfRef = useRef(null);

  let StartPdfExportProcess = async () => {
    setShowOverlay(true);
    if (resultData) MapResponse(resultData);
    else if (testResultId) {
      ApiHelper.getAsync(
        "ticket/GetTestCalculation",
        { id: testResultId },
        function (response) {
          if (response.data.statusCode) MapResponse(response.data.data);
          else
            dispatch(UpdateAlertModalData({ isShown: false, header: "calculationError", text: response.data.message }));
        },
        user.token
      );
    } else {
      setShowOverlay(false);
      alert("problem");
    }
  };

  let MapResponse = (result) => {
    let demography = {
      name: result.demography.name,
      email: result.demography.email,
      department: result.demography.department,
      faculty: result.demography.faculty,
      school: result.demography.school,
      birthYear: result.demography.birthYear,
      gender: result.demography.gender,
      experience: result.demography.experience,
    };

    let appData = {
      pdfCoverImageName: result.pdfCoverImageName,
      pdfInfoImageName: result.pdfInfoImageName,
      name: result.applicationName,
    };
    const scheme = JSON.parse(result.pdfJson);
    const calculation = JSON.parse(result.calculationJson);

    scheme.forEach((page) => HandleEachPage(page, calculation));
    setPdfComponent(
      <WholePdfFile
        StartDownloadPdf={HandlePrint}
        scheme={scheme}
        appData={appData}
        demography={demography}
        type="test"
        qrURL={CreateQrUrl()}
      />
    );
  };

  let HandleEachPage = (pageReportItems, calculation) => {
    pageReportItems.forEach((reportItem) => {
      if (reportItem.htmlEl) return;
      else if (reportItem.componentName === "TestScaleEntity") reportItem.score = calculation.testScore;
      else reportItem.score = calculation?.calculatedScopes.find((x) => x.scopeId === reportItem.scopeId)?.score;
    });
  };

  const CreateQrUrl = () => {
    return window.location.origin + "/#/tResult/" + testResultId;
  };

  const HandlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: docTitle, // 👈 this controls the suggested file name
    onBeforePrint: () => {
      setShowOverlay(false);
    },
    onAfterPrint: () => {
      setPdfComponent(null);
    },
  });

  // Start AI analysis - generate PDF and send to AI
  const handleAskAi = async () => {
    setIsGeneratingForAi(true);
    setShowOverlay(true);
    setAutoUploadFile(null);

    // Fetch data first if not already available
    if (resultData) {
      await generatePdfForAi(resultData);
    } else if (testResultId) {
      ApiHelper.getAsync(
        "ticket/GetTestCalculation",
        { id: testResultId },
        async function (response) {
          if (response.data.statusCode) {
            await generatePdfForAi(response.data.data);
          } else {
            setShowOverlay(false);
            setIsGeneratingForAi(false);
            dispatch(UpdateAlertModalData({ isShown: false, header: "calculationError", text: response.data.message }));
          }
        },
        user.token
      );
    } else {
      setShowOverlay(false);
      setIsGeneratingForAi(false);
      alert("problem");
    }
  };

  const generatePdfForAi = async (result) => {
    let demography = {
      name: result.demography.name,
      email: result.demography.email,
      department: result.demography.department,
      faculty: result.demography.faculty,
      school: result.demography.school,
      birthYear: result.demography.birthYear,
      gender: result.demography.gender,
      experience: result.demography.experience,
    };

    let appData = {
      pdfCoverImageName: result.pdfCoverImageName,
      pdfInfoImageName: result.pdfInfoImageName,
      name: result.applicationName,
    };
    const scheme = JSON.parse(result.pdfJson);
    const calculation = JSON.parse(result.calculationJson);

    scheme.forEach((page) => HandleEachPage(page, calculation));

    // Set component for rendering
    setPdfComponent(
      <WholePdfFile
        StartDownloadPdf={() => {}}
        scheme={scheme}
        appData={appData}
        demography={demography}
        type="test"
        qrURL={CreateQrUrl()}
      />
    );

    // Wait for component to render
    setTimeout(async () => {
      try {
        console.log("AI PDF generation - aiPdfRef.current:", aiPdfRef.current);
        if (aiPdfRef.current) {
          console.log("Generating PDF blob...");
          const pdfFile = await generatePdfBlob(
            aiPdfRef.current,
            `${docTitle || "test-report"}.pdf`
          );
          console.log("PDF file generated:", pdfFile.name, pdfFile.size, "bytes");
          setAutoUploadFile(pdfFile);
          setShowAiModal(true);
        } else {
          console.error("aiPdfRef.current is null - component not rendered yet");
        }
      } catch (error) {
        console.error("Error generating PDF for AI:", error);
      } finally {
        setShowOverlay(false);
        setIsGeneratingForAi(false);
        setPdfComponent(null);
      }
    }, 1500);
  };

  return (
    <>
      {showOverlay && <Overlay text={isGeneratingForAi ? "AI için PDF Hazırlanıyor" : "Pdf Dosyası Hazırlanıyor"} />}
      <div className="d-flex align-items-center">
        <DownloadButtonUi StartPdfExportProcess={StartPdfExportProcess} uiType={uiType} />
        <AskAiButtonUi onAskAi={handleAskAi} uiType={uiType} />
      </div>
      <AiResponseModal
        show={showAiModal}
        onHide={() => {
          setShowAiModal(false);
          setAutoUploadFile(null);
        }}
        autoUploadFile={autoUploadFile}
        analysisType="test"
      />
      <ComponentToPrint ref={componentRef}>
        <div className="onlyPrint" ref={componentRef}>
          {pdfComponent}
        </div>
      </ComponentToPrint>
      {/* Hidden container for AI PDF generation - must be visible for html2canvas */}
      {isGeneratingForAi && pdfComponent && (
        <div
          ref={aiPdfRef}
          style={{
            position: "fixed",
            left: "-9999px",
            top: 0,
            width: "210mm",
            background: "white",
          }}
        >
          {pdfComponent}
        </div>
      )}
    </>
  );
}
